import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, Inject, LOCALE_ID} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';

import {CalendarService} from './calendar.service';




export interface IEvent
{
	createTime	: Date;		//	created time
	updateTime	: Date;		//	latest updated time
	allDay		: boolean;	//	all day ?
	startTime	: Date;		//	start time UTC
	endTime		: Date;		//	end time UTC
	title		: string;	//	title
	ids		: string;	//	unique identity string
}
export class IEvent
{
	constructor( vCalEvent ?: any )
	{
		this.createTime	= null;
		this.updateTime	= null;
		this.allDay	= false;
		this.startTime	= null;
		this.endTime	= null;
		this.title	= null;
		this.ids	= null;

		if ( IEvent.isValid( vCalEvent ) )
		{
			//
			//	A Unix Time Stamp which is an integer value representing
			//	the number of milliseconds since January 1, 1970, 00:00:00 UTC (the Unix epoch),
			//	with leap seconds ignored.
			//	Keep in mind that most Unix timestamp functions are only accurate to the nearest second.
			//
			this.createTime	= new Date( vCalEvent.createTime.getTime() );
			this.updateTime	= new Date( vCalEvent.updateTime.getTime() );
			this.allDay	= vCalEvent.allDay;
			this.startTime	= new Date( vCalEvent.startTime.getTime() );
			this.endTime	= new Date( vCalEvent.endTime.getTime() );

			this.title	= vCalEvent.title;
			this.ids	= vCalEvent.ids;
		}
	}

	static isValid( pstCalEvent : any )
	{
		return ( null !== pstCalEvent && 'object' === typeof pstCalEvent ) &&
			pstCalEvent.createTime instanceof Date &&
			pstCalEvent.updateTime instanceof Date &&
			'boolean' === typeof pstCalEvent.allDay &&
			pstCalEvent.startTime instanceof Date &&
			pstCalEvent.endTime instanceof Date &&
			'string' === typeof pstCalEvent.title
		;
	}
}





export interface IRange
{
	startTime	: Date;
	endTime		: Date;
}

export interface IView
{
}

export interface IDayView extends IView
{
	allDayEvents	: IDisplayAllDayEvent[];
	rows		: IDayViewRow[];
}

export interface IDayViewRow
{
	events	: IDisplayEvent[];
	time	: Date;
}

export interface IMonthView extends IView
{
	dates		: IMonthViewRow[];
	dayHeaders	: string[];
}

export interface IMonthViewRow
{
	current		?: boolean;
	date		: Date;
	events		: IEvent[];
	hasEvent	?: boolean;
	label		: string;
	secondary	: boolean;
	selected	?: boolean;
	disabled	: boolean;
}

export interface IWeekView extends IView
{
	dates	: IWeekViewDateRow[];
	rows	: IWeekViewRow[][];
}

export interface IWeekViewDateRow
{
	current		?: boolean;
	date		: Date;
	events		: IDisplayEvent[];
	hasEvent	?: boolean;
	selected	?: boolean;
	dayHeader	: string;
}

export interface IWeekViewRow
{
	events	: IDisplayEvent[];
	time	: Date;
}

export interface IDisplayEvent
{
	endIndex	: number;
	endOffset	?: number;
	event		: IEvent;
	startIndex	: number;
	startOffset	?: number;
	overlapNumber	?: number;
	position	?: number;
}

export interface IDisplayWeekViewHeader
{
	viewDate	: IWeekViewDateRow;
}

export interface IDisplayAllDayEvent
{
	event	: IEvent;
}

export interface ICalendarComponent
{
	currentViewIndex	: number;
	direction		: number;
	eventSource		: IEvent[];
	getRange		: { (date: Date): IRange; };
	getViewData		: { (date: Date): IView };
	mode			: CalendarMode;
	range			: IRange;
	views			: IView[];
	onDataLoaded		: { (): void };
	onRangeChanged		: EventEmitter<IRange>;
}

export interface ITimeSelected
{
	events		: IEvent[];
	selectedTime	: Date;
	disabled	: boolean;
}

export interface IMonthViewDisplayEventTemplateContext
{
	view	: IView;
	row	: number;
	col	: number;
}

export interface IMonthViewEventDetailTemplateContext
{
	selectedDate	: ITimeSelected;
	noEventsLabel	: string;
}

export interface IWeekViewAllDayEventSectionTemplateContext
{
	day		: IWeekViewDateRow,
	eventTemplate	: TemplateRef<IDisplayAllDayEvent>
}

export interface IWeekViewNormalEventSectionTemplateContext
{
	tm		: IWeekViewRow,
	eventTemplate	: TemplateRef<IDisplayEvent>
}

export interface IDayViewAllDayEventSectionTemplateContext
{
	alldayEvents	: IDisplayAllDayEvent[],
	eventTemplate	: TemplateRef<IDisplayAllDayEvent>
}

export interface IDayViewNormalEventSectionTemplateContext
{
	tm		: IDayViewRow,
	eventTemplate	: TemplateRef<IDisplayEvent>
}

export interface IDateFormatter
{
	formatMonthViewDay		?: { (date: Date): string; };
	formatMonthViewDayHeader	?: { (date: Date): string; };
	formatMonthViewTitle		?: { (date: Date): string; };
	formatWeekViewDayHeader		?: { (date: Date): string; };
	formatWeekViewTitle		?: { (date: Date): string; };
	formatWeekViewHourColumn	?: { (date: Date): string; };
	formatDayViewTitle		?: { (date: Date): string; };
	formatDayViewHourColumn		?: { (date: Date): string; };
}

export type CalendarMode = 'day' | 'month' | 'week';

export type QueryMode = 'local' | 'remote';

export enum Step
{
	QuarterHour	= 15,
	HalfHour	= 30,
	Hour		= 60
}

@Component({
	selector: 'calendar',
	template: `
		<ng-template #monthviewDefaultDisplayEventTemplate let-view="view" let-row="row" let-col="col">
			<div class="calendar-date-box">
				<div class="calendar-date-name">{{ view.dates[row * 7 + col].label }}</div>
				<div class="calendar-date-event-info">{{ view.dates[row * 7 + col].events.length }}</div>
			</div>
		</ng-template>
		<ng-template #monthviewDefaultEventDetailTemplate let-showEventDetail="showEventDetail"
			     let-selectedDate="selectedDate" let-noEventsLabel="noEventsLabel">
			<ion-list class="event-detail-container" has-bouncing="false" *ngIf="showEventDetail"
				  overflow-scroll="false">
				<ion-item *ngFor="let event of selectedDate?.events" (click)="eventSelected(event)">
                        <span *ngIf="!event.allDay"
			      class="monthview-eventdetail-timecolumn">{{event.startTime|date: 'HH:mm'}}
				-
				{{event.endTime|date: 'HH:mm'}}
                        </span>
					<span *ngIf="event.allDay"
					      class="monthview-eventdetail-timecolumn">{{allDayLabel}}</span>
					<span class="event-detail">  |  {{event.title}}</span>
				</ion-item>
				<ion-item *ngIf="selectedDate?.events.length==0">
					<div class="no-events-label">{{noEventsLabel}}</div>
				</ion-item>
			</ion-list>
		</ng-template>
		<ng-template #defaultWeekviewHeaderTemplate let-viewDate="viewDate">
			{{ viewDate.dayHeader }}
		</ng-template>
		<ng-template #defaultAllDayEventTemplate let-displayEvent="displayEvent">
			<div class="calendar-event-inner">{{displayEvent.event.title}}</div>
		</ng-template>
		<ng-template #defaultNormalEventTemplate let-displayEvent="displayEvent">
			<div class="calendar-event-inner">{{displayEvent.event.title}}</div>
		</ng-template>
		<ng-template #defaultWeekViewAllDayEventSectionTemplate let-day="day" let-eventTemplate="eventTemplate">
			<div [ngClass]="{'calendar-event-wrap': day.events}" *ngIf="day.events"
			     [ngStyle]="{height: 25*day.events.length+'px'}">
				<div *ngFor="let displayEvent of day.events" class="calendar-event" tappable
				     (click)="eventSelected(displayEvent.event)"
				     [ngStyle]="{top: 25*displayEvent.position+'px', width: 100*(displayEvent.endIndex-displayEvent.startIndex)+'%', height: '25px'}">
					<ng-template [ngTemplateOutlet]="eventTemplate"
						     [ngTemplateOutletContext]="{displayEvent:displayEvent}">
					</ng-template>
				</div>
			</div>
		</ng-template>
		<ng-template #defaultDayViewAllDayEventSectionTemplate let-allDayEvents="allDayEvents"
			     let-eventTemplate="eventTemplate">
			<div *ngFor="let displayEvent of allDayEvents; let eventIndex=index"
			     class="calendar-event" tappable
			     (click)="eventSelected(displayEvent.event)"
			     [ngStyle]="{top: 25*eventIndex+'px',width: '100%',height:'25px'}">
				<ng-template [ngTemplateOutlet]="eventTemplate"
					     [ngTemplateOutletContext]="{displayEvent:displayEvent}">
				</ng-template>
			</div>
		</ng-template>
		<ng-template #defaultNormalEventSectionTemplate let-tm="tm" let-hourParts="hourParts"
			     let-eventTemplate="eventTemplate">
			<div [ngClass]="{'calendar-event-wrap': tm.events}" *ngIf="tm.events">
				<div *ngFor="let displayEvent of tm.events" class="calendar-event" tappable
				     (click)="eventSelected(displayEvent.event)"
				     [ngStyle]="{top: (37*displayEvent.startOffset/hourParts)+'px',left: 100/displayEvent.overlapNumber*displayEvent.position+'%', width: 100/displayEvent.overlapNumber+'%', height: 37*(displayEvent.endIndex -displayEvent.startIndex - (displayEvent.endOffset + displayEvent.startOffset)/hourParts)+'px'}">
					<ng-template [ngTemplateOutlet]="eventTemplate"
						     [ngTemplateOutletContext]="{displayEvent:displayEvent}">
					</ng-template>
				</div>
			</div>
		</ng-template>

		<div [ngSwitch]="calendarMode" class="{{calendarMode}}view-container">
			<monthview *ngSwitchCase="'month'"
				   [formatDay]="formatDay"
				   [formatDayHeader]="formatDayHeader"
				   [formatMonthTitle]="formatMonthTitle"
				   [startingDayMonth]="startingDayMonth"
				   [showEventDetail]="showEventDetail"
				   [noEventsLabel]="noEventsLabel"
				   [autoSelect]="autoSelect"
				   [eventSource]="eventSource"
				   [markDisabled]="markDisabled"
				   [monthviewDisplayEventTemplate]="monthviewDisplayEventTemplate||monthviewDefaultDisplayEventTemplate"
				   [monthviewInactiveDisplayEventTemplate]="monthviewInactiveDisplayEventTemplate||monthviewDefaultDisplayEventTemplate"
				   [monthviewEventDetailTemplate]="monthviewEventDetailTemplate||monthviewDefaultEventDetailTemplate"
				   [locale]="locale"
				   [dateFormatter]="dateFormatter"
				   [dir]="dir"
				   [lockSwipeToPrev]="lockSwipeToPrev"
				   [lockSwipes]="lockSwipes"
				   [spaceBetween]="spaceBetween"
				   (onRangeChanged)="rangeChanged($event)"
				   (onEventSelected)="eventSelected($event)"
				   (onTimeSelected)="timeSelected($event)"
				   (onTitleChanged)="titleChanged($event)">
			</monthview>
			<weekview *ngSwitchCase="'week'"
				  [formatWeekTitle]="formatWeekTitle"
				  [formatWeekViewDayHeader]="formatWeekViewDayHeader"
				  [formatHourColumn]="formatHourColumn"
				  [startingDayWeek]="startingDayWeek"
				  [allDayLabel]="allDayLabel"
				  [hourParts]="hourParts"
				  [autoSelect]="autoSelect"
				  [hourSegments]="hourSegments"
				  [eventSource]="eventSource"
				  [markDisabled]="markDisabled"
				  [weekviewHeaderTemplate]="weekviewHeaderTemplate||defaultWeekviewHeaderTemplate"
				  [weekviewAllDayEventTemplate]="weekviewAllDayEventTemplate||defaultAllDayEventTemplate"
				  [weekviewNormalEventTemplate]="weekviewNormalEventTemplate||defaultNormalEventTemplate"
				  [weekviewAllDayEventSectionTemplate]="weekviewAllDayEventSectionTemplate||defaultWeekViewAllDayEventSectionTemplate"
				  [weekviewNormalEventSectionTemplate]="weekviewNormalEventSectionTemplate||defaultNormalEventSectionTemplate"
				  [locale]="locale"
				  [dateFormatter]="dateFormatter"
				  [dir]="dir"
				  [scrollToHour]="scrollToHour"
				  [preserveScrollPosition]="preserveScrollPosition"
				  [lockSwipeToPrev]="lockSwipeToPrev"
				  [lockSwipes]="lockSwipes"
				  [startHour]="startHour"
				  [endHour]="endHour"
				  [spaceBetween]="spaceBetween"
				  (onRangeChanged)="rangeChanged($event)"
				  (onEventSelected)="eventSelected($event)"
				  (onTimeSelected)="timeSelected($event)"
				  (onTitleChanged)="titleChanged($event)">
			</weekview>
			<dayview *ngSwitchCase="'day'"
				 [formatDayTitle]="formatDayTitle"
				 [formatHourColumn]="formatHourColumn"
				 [allDayLabel]="allDayLabel"
				 [hourParts]="hourParts"
				 [hourSegments]="hourSegments"
				 [eventSource]="eventSource"
				 [markDisabled]="markDisabled"
				 [dayviewAllDayEventTemplate]="dayviewAllDayEventTemplate||defaultAllDayEventTemplate"
				 [dayviewNormalEventTemplate]="dayviewNormalEventTemplate||defaultNormalEventTemplate"
				 [dayviewAllDayEventSectionTemplate]="dayviewAllDayEventSectionTemplate||defaultDayViewAllDayEventSectionTemplate"
				 [dayviewNormalEventSectionTemplate]="dayviewNormalEventSectionTemplate||defaultNormalEventSectionTemplate"
				 [locale]="locale"
				 [dateFormatter]="dateFormatter"
				 [dir]="dir"
				 [scrollToHour]="scrollToHour"
				 [preserveScrollPosition]="preserveScrollPosition"
				 [lockSwipeToPrev]="lockSwipeToPrev"
				 [lockSwipes]="lockSwipes"
				 [startHour]="startHour"
				 [endHour]="endHour"
				 [spaceBetween]="spaceBetween"
				 (onRangeChanged)="rangeChanged($event)"
				 (onEventSelected)="eventSelected($event)"
				 (onTimeSelected)="timeSelected($event)"
				 (onTitleChanged)="titleChanged($event)">
			</dayview>
		</div>
	`,
	styles: [`
		:host > div {
			height: 100%;
		}

		.event-detail-container {
			border-top: 2px darkgrey solid;
		}

		.no-events-label {
			font-weight: bold;
			color: darkgrey;
			text-align: center;
		}

		.event-detail {
			cursor: pointer;
			white-space: nowrap;
			text-overflow: ellipsis;
		}

		.monthview-eventdetail-timecolumn {
			width: 110px;
			overflow: hidden;
		}

		.calendar-event-inner {
			overflow: hidden;
			background-color: #3a87ad;
			color: white;
			height: 100%;
			width: 100%;
			padding: 2px;
			line-height: 15px;
		}

		@media (max-width: 750px) {
			.calendar-event-inner {
				font-size: 12px;
			}
		}
	`],
	providers: [CalendarService]
})
export class CalendarComponent implements OnInit
{
	@Input()
	get currentDate(): Date
	{
		return this._currentDate;
	}

	set currentDate( val: Date )
	{
		if ( ! val )
		{
			val = new Date();
		}

		this._currentDate = val;
		this.m_oCalendarService.setCurrentDate( val, true );
		this.onCurrentDateChanged.emit( this._currentDate );
	}

	@Input() eventSource : IEvent[]			= [];
	@Input() calendarMode : CalendarMode		= 'month';
	@Input() formatDay : string			= 'd';
	@Input() formatDayHeader : string		= 'EEE';
	@Input() formatDayTitle : string		= 'MMMM dd, yyyy';
	@Input() formatWeekTitle : string		= 'MMMM yyyy, \'Week\' w';
	@Input() formatMonthTitle : string		= 'MMMM yyyy';
	@Input() formatWeekViewDayHeader : string	= 'EEE d';
	@Input() formatHourColumn : string		= 'ha';
	@Input() showEventDetail : boolean		= true;
	@Input() startingDayMonth : number		= 0;
	@Input() startingDayWeek : number		= 0;
	@Input() allDayLabel : string			= 'all day';
	@Input() noEventsLabel : string			= 'No Events';
	@Input() queryMode : QueryMode			= 'local';
	@Input() step : Step				= Step.Hour;
	@Input() timeInterval : number			= 60;
	@Input() autoSelect : boolean			= true;
	@Input() markDisabled : ( date : Date )		=> boolean;
	@Input() monthviewDisplayEventTemplate : TemplateRef<IMonthViewDisplayEventTemplateContext>;
	@Input() monthviewInactiveDisplayEventTemplate: TemplateRef<IMonthViewDisplayEventTemplateContext>;
	@Input() monthviewEventDetailTemplate: TemplateRef<IMonthViewEventDetailTemplateContext>;
	@Input() weekviewHeaderTemplate: TemplateRef<IDisplayWeekViewHeader>;
	@Input() weekviewAllDayEventTemplate: TemplateRef<IDisplayAllDayEvent>;
	@Input() weekviewNormalEventTemplate: TemplateRef<IDisplayEvent>;
	@Input() dayviewAllDayEventTemplate: TemplateRef<IDisplayAllDayEvent>;
	@Input() dayviewNormalEventTemplate: TemplateRef<IDisplayEvent>;
	@Input() weekviewAllDayEventSectionTemplate: TemplateRef<IWeekViewAllDayEventSectionTemplateContext>;
	@Input() weekviewNormalEventSectionTemplate: TemplateRef<IWeekViewNormalEventSectionTemplateContext>;
	@Input() dayviewAllDayEventSectionTemplate: TemplateRef<IDayViewAllDayEventSectionTemplateContext>;
	@Input() dayviewNormalEventSectionTemplate: TemplateRef<IDayViewNormalEventSectionTemplateContext>;
	@Input() dateFormatter: IDateFormatter;
	@Input() dir: string = "";
	@Input() scrollToHour: number = 0;
	@Input() preserveScrollPosition: boolean = false;
	@Input() lockSwipeToPrev: boolean = false;
	@Input() lockSwipes: boolean = false;
	@Input() locale: string = "";
	@Input() startHour: number = 0;
	@Input() endHour: number = 24;
	@Input() spaceBetween: number = 0;

	@Output() onCurrentDateChanged = new EventEmitter<Date>();
	@Output() onRangeChanged = new EventEmitter<IRange>();
	@Output() onEventSelected = new EventEmitter<IEvent>();
	@Output() onTimeSelected = new EventEmitter<ITimeSelected>();
	@Output() onTitleChanged = new EventEmitter<string>();

	private _currentDate: Date;
	private hourParts = 1;
	private hourSegments = 1;
	private currentDateChangedFromChildrenSubscription: Subscription;

	constructor(private m_oCalendarService : CalendarService,
		    @Inject(LOCALE_ID) private m_sAppLocale : string)
	{
		this.locale = m_sAppLocale;
	}

	ngOnInit()
	{
		if ( this.autoSelect )
		{
			if ( 'false' === this.autoSelect.toString() )
			{
				this.autoSelect = false;
			}
			else
			{
				this.autoSelect = true;
			}
		}

		this.hourSegments	= 60 / this.timeInterval;
		this.hourParts		= 60 / this.step;

		if ( this.hourParts <= this.hourSegments )
		{
			this.hourParts	= 1;
		}
		else
		{
			this.hourParts	= this.hourParts / this.hourSegments;
		}
		this.startHour	= parseInt( this.startHour.toString() );
		this.endHour	= parseInt( this.endHour.toString() );
		this.m_oCalendarService.queryMode	= this.queryMode;

		this.currentDateChangedFromChildrenSubscription = this.m_oCalendarService.currentDateChangedFromChildren$.subscribe(currentDate =>
		{
			this._currentDate = currentDate;
			this.onCurrentDateChanged.emit( currentDate );
		});
	}

	ngOnDestroy()
	{
		if ( this.currentDateChangedFromChildrenSubscription )
		{
			this.currentDateChangedFromChildrenSubscription.unsubscribe();
			this.currentDateChangedFromChildrenSubscription = null;
		}
	}

	rangeChanged( range : IRange )
	{
		this.onRangeChanged.emit( range );
	}

	eventSelected( event : IEvent )
	{
		this.onEventSelected.emit( event );
	}

	timeSelected( timeSelected: ITimeSelected )
	{
		this.onTimeSelected.emit( timeSelected );
	}

	titleChanged( title : string )
	{
		this.onTitleChanged.emit( title );
	}

	loadEvents()
	{
		this.m_oCalendarService.loadEvents();
	}
}